// Lightweight GA4 helper for client-side usage

type Gtag = (
  command: 'js' | 'config' | 'event' | 'consent' | 'get',
  targetOrDate?: any,
  paramsOrFieldName?: any,
  callback?: (val: any) => void
) => void

declare global {
  interface Window {
    dataLayer?: any[]
    gtag?: Gtag
    __gaInited?: boolean
    __gaCidCache?: string
    // no consent helpers (events not limited)
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || ''
const SITE_ENV = process.env.NEXT_PUBLIC_SITE_ENV || 'development'
const IS_PROD = SITE_ENV === 'production'

function ensureGtag() {
  if (typeof window === 'undefined') return false
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function gtag() { (window.dataLayer as any).push(arguments as any) } as any
  return true
}

export function initGA() {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !GA_ID) return
  if (window.__gaInited) return
  if (!ensureGtag()) return

  // Inject tag
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`
  document.head.appendChild(s)

  window.gtag!('js', new Date())
  // Disable auto page_view to control SPA navigations manually
  window.gtag!('config', GA_ID, { send_page_view: false })

  window.__gaInited = true
}

export function pageview(url?: string) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !GA_ID || !window.gtag) return
  const loc = url || (typeof window !== 'undefined' ? window.location.href : undefined)
  window.gtag('event', 'page_view', {
    page_location: loc,
  })
}

export function gaEvent(name: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !GA_ID || !window.gtag) return
  window.gtag('event', name as any, params || {})
}

// Best-effort client id getter
export async function getGAClientId(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined
  if (!IS_PROD || !GA_ID) return undefined
  if (!ensureGtag()) return undefined

  // Cached
  if (window.__gaCidCache) return window.__gaCidCache

  // Try GA provided client_id
  try {
    const cid = await new Promise<string | undefined>((resolve) => {
      try {
        window.gtag!('get', GA_ID, 'client_id', (id: string) => resolve(id || undefined))
      } catch { resolve(undefined) }
      // Safety timeout
      setTimeout(() => resolve(undefined), 1200)
    })
    if (cid) { window.__gaCidCache = cid; return cid }
  } catch {}

  // Fallback: stable temp id in localStorage (consent-safe, non-cookie)
  try {
    const key = 'ga_temp_cid'
    let v = localStorage.getItem(key) || ''
    if (!v) {
      v = 'temp_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
      localStorage.setItem(key, v)
    }
    window.__gaCidCache = v
    return v
  } catch {}
  return undefined
}

// Simple mapper for ecommerce events from our app
export function fireEcommerceFromApp(event: string, props: Record<string, any> = {}) {
  if (!IS_PROD) return
  switch (event) {
    case 'cart_viewed': {
      const items = Array.isArray(props.cart_items)
        ? props.cart_items.map((it: any) => ({
            item_id: toId(it.product_handle, it.variant_id, it.product_id),
            item_name: toName(it.product_title, it.product_handle),
            price: toNum(it.price),
            quantity: toNum(it.quantity) || 1,
          }))
        : []
      const value = toNum(props.cart_total) || items.reduce((n, it) => n + (toNum(it.price) * (it.quantity || 1)), 0)
      gaEvent('view_cart', { currency: 'MXN', value, items })
      break
    }
    case 'product_viewed': {
      const price = toNum(props.price)
      const id = toId(props.product_handle, props.variant_id, props.product_id)
      const name = toName(props.product_title, props.product_handle)
      const item = {
        item_id: id,
        item_name: name,
        item_variant: props.variant_title || props.variant_type || undefined,
        price,
        quantity: 1,
      }
      gaEvent('view_item', { currency: 'MXN', value: item.price || 0, items: [item] })
      break
    }
    case 'add_to_cart': {
      const price = toNum(props.price)
      const qty = toNum(props.quantity) || 1
      const id = toId(props.product_handle, props.variant_id, props.product_id)
      const name = toName(props.product_title, props.product_handle)
      const item = {
        item_id: id,
        item_name: name,
        item_variant: props.variant_title || undefined,
        price,
        quantity: qty,
      }
      gaEvent('add_to_cart', { currency: 'MXN', value: (item.price || 0) * (item.quantity || 1), items: [item] })
      break
    }
    case 'remove_from_cart': {
      const price = toNum(props.price)
      const qty = toNum(props.quantity) || 1
      const id = toId(props.product_handle, props.variant_id, props.product_id)
      const name = toName(props.product_title, props.product_handle)
      const item = {
        item_id: id,
        item_name: name,
        item_variant: props.variant_title || undefined,
        price,
        quantity: qty,
      }
      gaEvent('remove_from_cart', { currency: 'MXN', value: (item.price || 0) * (item.quantity || 1), items: [item] })
      break
    }
    case 'checkout_initiated': {
      const items = Array.isArray(props.items)
        ? props.items.map((it: any) => ({
            item_id: toId(it.product_handle, it.variant_id, it.product_id),
            item_name: toName(it.product_title, it.product_handle),
            item_variant: it.variant_title || undefined,
            price: toNum(it.price),
            quantity: toNum(it.quantity) || 1,
          }))
        : []
      // Ensure at least one item to keep GA from discarding
      const itemsPayload = items.length > 0
        ? items
        : [{ item_id: toId(props.product_handle, props.variant_id, props.product_id), item_name: toName(props.product_title, props.product_handle), price: toNum(props.price), quantity: 1 }]
      gaEvent('begin_checkout', {
        currency: 'MXN',
        value: toNum(props.cart_total),
        coupon: props.discount_code || undefined,
        items: itemsPayload,
        // Ensure send on navigation
        transport_type: 'beacon',
      })
      break
    }
    case 'checkout_form_shown': {
      const items = Array.isArray(props.items)
        ? props.items.map((it: any) => ({
            item_id: toId(it.product_handle, it.variant_id, it.product_id),
            item_name: toName(it.product_title, it.product_handle),
            item_variant: it.variant_title || undefined,
            price: toNum(it.price),
            quantity: toNum(it.quantity) || 1,
          }))
        : []
      const itemsPayload = items.length ? items : undefined
      gaEvent('add_shipping_info', { currency: 'MXN', value: toNum(props.cart_total), items: itemsPayload })
      break
    }
    case 'checkout_continue_clicked': {
      const items = Array.isArray(props.items)
        ? props.items.map((it: any) => ({
            item_id: toId(it.product_handle, it.variant_id, it.product_id),
            item_name: toName(it.product_title, it.product_handle),
            item_variant: it.variant_title || undefined,
            price: toNum(it.price),
            quantity: toNum(it.quantity) || 1,
          }))
        : []
      const itemsPayload = items.length ? items : undefined
      gaEvent('add_payment_info', { currency: 'MXN', value: toNum(props.cart_total), items: itemsPayload, transport_type: 'beacon' })
      break
    }
    case 'newsletter_signup_completed': {
      gaEvent('sign_up', { method: 'footer_newsletter' })
      break
    }
    case 'cta_clicked': {
      gaEvent('select_promotion', {
        creative_name: props.cta_text,
        location_id: props.location,
        promotion_id: props.section || undefined,
      })
      break
    }
    case 'perfil_quiz_started':
      gaEvent('perfil_quiz_started', { source: props.source })
      break
    case 'perfil_quiz_completed':
      gaEvent('perfil_quiz_completed')
      break
    case 'perfil_email_submitted':
      gaEvent('perfil_email_submitted', { email_domain: props.email_domain })
      break
    case 'perfil_report_viewed':
      gaEvent('perfil_report_viewed', { lead_id: props.lead_id, returning: props.returning })
      break
    case 'qualified_perfil_capilar_lead':
      gaEvent('qualified_perfil_capilar_lead', { profile: props.profile, profile_name: props.profile_name, confidence: props.confidence })
      break
    case 'perfil_report_cta_clicked':
      gaEvent('perfil_report_cta_clicked', { lead_id: props.lead_id })
      break
    default:
      break
  }
}

function toNum(v: any): number { const n = Number(v); return isFinite(n) ? n : 0 }
function toId(...vals: any[]): string { for (const v of vals) { if (v) return String(v) } return 'unknown' }
function toName(...vals: any[]): string { for (const v of vals) { if (v) return String(v) } return 'unknown' }
